<!-- src/components/TodoSection.vue -->
<template>
    <div class="todo-section mb-3">
      <h4 class="h6 mb-2 text-primary">{{ title }}</h4>

      <!-- Items List -->
      <ul v-if="items && items.length > 0" class="list-unstyled todo-list-items mb-2">
        <!-- *** USE GLOBALLY UNIQUE KEY *** -->
        <li v-for="(item, index) in items" :key="`${parentId}-${title}-${item.name}-${index}`" class="todo-item d-flex justify-content-between align-items-center py-1 border-bottom">
          <span class="item-name small flex-grow-1 me-2">{{ item.name }}</span>
          <div class="checkboxes btn-group btn-group-sm" role="group">
              <!-- Use unique IDs incorporating parentId and index -->
              <input type="checkbox" class="btn-check" :id="`${parentId}-${title}-req-${index}`" :checked="item.requested" @change="updateItem(index, 'requested')" autocomplete="off">
              <label class="btn btn-outline-secondary" :for="`${parentId}-${title}-req-${index}`" title="Demandé">D</label>

              <input type="checkbox" class="btn-check" :id="`${parentId}-${title}-done-${index}`" :checked="item.done" @change="updateItem(index, 'done')" autocomplete="off">
              <label class="btn btn-outline-success" :for="`${parentId}-${title}-done-${index}`" title="Fait">F</label>

              <input type="checkbox" class="btn-check" :id="`${parentId}-${title}-retr-${index}`" :checked="item.retrieved" @change="updateItem(index, 'retrieved')" autocomplete="off">
              <label class="btn btn-outline-info" :for="`${parentId}-${title}-retr-${index}`" title="Récupéré">R</label>
          </div>
        </li>
      </ul>
      <p v-else class="no-items text-muted small">Aucun item.</p>
  
      <!-- Add Custom Item Form -->
      <div v-if="canAdd" class="add-item-form custom-add input-group input-group-sm mb-2">
        <input type="text" class="form-control" v-model="newItemName" @keyup.enter="addCustomItem" placeholder="Ajouter tâche perso...">
        <button class="btn btn-outline-primary" @click="addCustomItem" :disabled="!newItemName.trim()" type="button">+</button>
      </div>
  
      <!-- Preset Buttons -->
      <div v-if="availablePresets.length > 0" class="preset-buttons-container mt-2 pt-2 border-top">
          <label class="form-label small mb-1">Ajouter un preset :</label>
          <div class="preset-buttons">
              <button
                  v-for="preset in availablePresets"
                  :key="preset"
                  @click="addPresetItem(preset)"
                  class="btn btn-sm btn-outline-secondary preset-button"
                  type="button"
               >
                  {{ preset }}
              </button>
          </div>
      </div>
       <p v-else-if="presets && presets.length > 0 && items && items.length >= presets.length" class="no-presets-left small text-muted mt-2 pt-2 border-top">
           Tous les presets standards ajoutés.
       </p>
  
    </div>
  </template>
  
  <script setup>
  import { ref, computed } from 'vue';
  
  // --- Props ---
  const props = defineProps({
    title: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      default: () => [] // Default to empty array
    },
    canAdd: { // Prop to allow adding custom items
      type: Boolean,
      default: false
    },
    presets: { // Prop for the list of standard preset names
      type: Array,
      default: () => []
    },
    parentId: { type: [String, Number], required: true }
  });
  
  // --- Emits ---
  const emit = defineEmits(['update:item', 'add:item']);
  
  // --- State ---
  const newItemName = ref('');
  
  // --- Helper Functions ---
  /**
   * Checks if an item with the given name already exists in the list (case-insensitive).
   * @param {string} nameToCheck - The name of the item to check.
   * @returns {boolean} True if the item exists, false otherwise.
   */
  const itemExists = (nameToCheck) => {
      if (!props.items || props.items.length === 0) return false;
      const lowerCaseName = nameToCheck.toLowerCase();
      return props.items.some(item => item.name.toLowerCase() === lowerCaseName);
  };
  
  // --- Computed Properties ---
  /**
   * Filters the presets array to show only those not already present in the items list.
   */
  const availablePresets = computed(() => {
      if (!props.presets || props.presets.length === 0) return [];
      if (!props.items || props.items.length === 0) return props.presets; // If no items yet, all presets are available
  
      const currentItemNamesLower = props.items.map(item => item.name.toLowerCase());
      return props.presets.filter(preset => !currentItemNamesLower.includes(preset.toLowerCase()));
  });
  
  
  // --- Methods ---
  /**
   * Handles checkbox changes and emits the updated item.
   * Includes logic for auto-checking/unchecking related statuses.
   * @param {number} index - The index of the item being updated.
   * @param {'requested' | 'done' | 'retrieved'} field - The checkbox field being changed.
   */
   const updateItem = (index, field) => {
    const originalItem = props.items[index];
    if (!originalItem) {
        console.error(`TodoSection: Cannot find original item at index ${index} to update.`);
        return;
    }

    // *** Create a copy of ONLY the item being changed ***
    const updatedItemCopy = { ...originalItem };

    // Toggle the boolean field on the copy
    updatedItemCopy[field] = !updatedItemCopy[field];

    // Auto-checking/unchecking logic (applied to the copy)
    if (field === 'done' && updatedItemCopy.done) {
        updatedItemCopy.requested = true;
    } else if (field === 'requested' && !updatedItemCopy.requested) {
        updatedItemCopy.done = false;
        updatedItemCopy.retrieved = false;
    }
    if (field === 'retrieved' && updatedItemCopy.retrieved) {
        updatedItemCopy.requested = true;
        updatedItemCopy.done = true;
    } else if (field === 'done' && !updatedItemCopy.done) {
        updatedItemCopy.retrieved = false;
    }

    // *** Emit the original index and the modified COPY of the item ***
    emit('update:item', { index, updatedItem: updatedItemCopy });
};
  
  /**
   * Handles adding a custom item from the input field.
   */
  const addCustomItem = () => {
    const name = newItemName.value.trim();
    if (name && !itemExists(name)) {
      emit('add:item', name); // Parent component will create the object
      newItemName.value = ''; // Clear input
    } else if (name) {
        // Optionally provide user feedback (e.g., using a toast notification library)
        console.warn(`Custom item "${name}" already exists in this list.`);
        // alert(`"${name}" existe déjà dans cette liste.`);
    }
  };
  
  /**
   * Handles adding an item when a preset button is clicked.
   * @param {string} presetName - The name of the preset item to add.
   */
  const addPresetItem = (presetName) => {
      // Double-check existence, although availablePresets should filter it
      if (presetName && !itemExists(presetName)) {
          emit('add:item', presetName); // Parent component will create the object
      } else {
           console.warn(`Preset item "${presetName}" already exists or is invalid.`);
      }
  };
  
  </script>
  
  <style scoped>
  /* Styles specific to TodoSection */
  .todo-section {
      margin-bottom: 15px; /* Space below each section */
       padding: 10px; /* Add some internal padding */
       border: 1px solid var(--light-gray, #f0f0f0); /* Optional border */
       border-radius: 4px;
       background-color: #fff; /* Ensure background contrasts if parent changes */
  }
  
  .todo-section h4 {
      display: block;
      margin: 0 0 12px 0; /* Increase space below title */
      color: var(--secondary-color, #007bff);
      font-size: 1.05rem; /* Slightly larger title */
      border-bottom: 1px solid var(--medium-gray, #e9ecef);
      padding-bottom: 8px;
      font-weight: 600;
  }
  
  .todo-list-items {
      list-style: none;
      padding: 0;
      margin: 0 0 10px 0; /* Space below the list */
  }
  
  .todo-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 4px; /* Adjust padding */
      border-bottom: 1px dotted var(--medium-gray, #eee);
      font-size: 0.95rem;
  }
  .todo-item:last-child {
      border-bottom: none;
  }
  
  .item-name {
      flex-grow: 1;
      margin-right: 15px; /* More space before checkboxes */
      word-break: break-word;
  }
  
  .checkboxes {
      display: flex;
      gap: 15px; /* More space between D/F/R */
      white-space: nowrap;
      align-items: center; /* Align checkboxes vertically */
  }
  
  .checkboxes label {
      margin: 0;
      font-size: 0.85em; /* Slightly smaller labels for D/F/R */
      display: flex;
      align-items: center;
      cursor: pointer;
      color: var(--dark-gray);
      font-weight: 500;
  }
  
  .checkboxes input[type="checkbox"] {
      margin-right: 5px;
      transform: scale(1.0); /* Keep size reasonable */
      accent-color: var(--primary-color, #0056b3);
      cursor: pointer;
      vertical-align: middle; /* Align checkbox better with text */
      margin-top: -1px; /* Fine-tune alignment */
  }
  
  .no-items {
      font-style: italic;
      color: var(--dark-gray);
      font-size: 0.9em;
      padding: 5px 0;
  }
  
  /* Custom Add Form */
  .add-item-form.custom-add {
      margin-top: 15px;
      margin-bottom: 15px; /* Space below custom add */
  }
  .add-item-form { display: flex; gap: 5px; }
  .add-item-form input { flex-grow: 1; font-size: 0.9rem; padding: 6px 10px; border: 1px solid var(--border-color); border-radius: 3px; }
  .add-item-form button { padding: 6px 12px; font-size: 1rem; line-height: 1; background-color: var(--secondary-color); color: white; border: none; border-radius: 4px; cursor: pointer; }
  .add-item-form button:disabled { background-color: #ccc; }
  
  
  /* Preset Buttons */
  .preset-buttons-container {
      margin-top: 15px;
      padding-top: 15px; /* More space above presets */
      border-top: 1px solid var(--medium-gray);
  }
  .preset-buttons-container label {
      font-size: 0.85rem;
      color: var(--dark-gray);
      margin-bottom: 8px;
      display: block;
      font-weight: 500;
  }
  .preset-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 8px; /* More space between buttons */
  }
  .preset-button {
      padding: 4px 12px; /* Slightly more padding */
      font-size: 0.8rem;
      line-height: 1.3;
      background-color: var(--light-gray);
      color: var(--primary-color);
      border: 1px solid var(--border-color);
      border-radius: 15px; /* More rounded */
      cursor: pointer;
      transition: background-color 0.2s, border-color 0.2s, color 0.2s, box-shadow 0.1s;
      white-space: nowrap;
  }
  .preset-button:hover {
      background-color: var(--medium-gray);
      border-color: var(--primary-color);
      color: #000;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }
  
  .no-presets-left {
      font-size: 0.85rem;
      color: var(--dark-gray);
      font-style: italic;
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px solid var(--light-gray);
  }
  </style>